import { Flex, TableContainer, Table, Thead, Tr, Th, Tbody, Td, Switch, Link as ChakraLink } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab, CopyButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import QrCodeModal from '../../../../components/qrCodeModal/QrCodeModal'
import ShortText from '../../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast, usePendingRows } from '../../../../helpers'
import { IUrlRedirector, UrlRedirectorService } from '../../../../services/UrlShortener/UrlRedirectorService'
import { UrlRedirectorCreateModal } from './UrlRedirectorCreateModal'
import { UrlRedirectorEditModal } from './UrlRedirectorEditModal'

function UrlRedirectorPage() {
  const service = new UrlRedirectorService()
  const [urls, setUrls] = useState<IUrlRedirector[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    length: DEFAULT_PAGE_SIZE,
    search: { value: '' }
  })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [editingRecord, setEditingRecord] = useState<number | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null })
  const toast = useAppToast()
  const [creatingRecord, setCreatingRecord] = useState<boolean>(false)
  const [copyingData, setCopyingData] = useState<Partial<IUrlRedirector> | undefined>(undefined)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const hasRole = useHasRole()
  const permissions = {
    addUrl: hasRole('add_marketing_url'),
    deleteUrl: hasRole('delete_marketing_url'),
    toggleUrl: hasRole('toggle_marketing_url')
  }

  const fetchUrls = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.list(filters.search.value, page, filters.length)
      setTotalPages(Math.ceil(data.recordsFiltered / filters.length))
      setUrls(data.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os redirecionamentos', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUrls()
  }, [page])

  useEffect(() => {
    !editingRecord && !creatingRecord && fetchUrls()
  }, [editingRecord, creatingRecord])

  useEffect(() => {
    // A busca já chega debounced do `FilterSearch`.
    fetchUrls()
  }, [filters.search.value, filters.length])

  const savingRow = usePendingRows()

  const toggleActive = async (url: IUrlRedirector, active: boolean) => {
    try {
      await service.updateActive(url.id, active)

      toast.success(`O redirecionador de URL foi atualizado com sucesso`)

      setUrls((prev) => prev.map((item) => (item.id === url.id ? { ...item, active } : item)))
    } catch (error) {
      toast.error('Não foi possível atualizar o redirecionador de URL')
    }
  }

  const handleDelete = async (id: number) => {
    setDeleteDialog({ open: true, id })
  }

  const confirmDelete = async () => {
    if (!deleteDialog.id) return
    try {
      await service.delete(deleteDialog.id)
      toast.success('URL removida com sucesso')
      fetchUrls()
    } catch (e) {
      toast.error('Erro ao remover URL', e)
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  const handleCopy = (url: IUrlRedirector) => {
    setCopyingData(url)
    setCreatingRecord(true)
  }

  const handleCloseCreateModal = () => {
    setCreatingRecord(false)
    setCopyingData(undefined)
  }

  return (
    <>
      <FilterBar
        onClear={() => {
          setFilters({ ...filters, search: { value: '' } })
          setPage(1)
        }}
        isDirty={filters.search.value !== ''}
      >
        <FilterSearch
          value={filters.search.value}
          onChange={(value) => {
            setFilters({ ...filters, search: { value } })
            setPage(1)
          }}
          placeholder="Buscar por origem ou destino"
        />
      </FilterBar>
      <TableContainer>
        <Table padding="0" mt="5">
          <Thead>
            <Tr>
              <Th>De</Th>
              <Th>Para</Th>
              <Th>Criado Por</Th>
              <Th>Data de Criação</Th>
              <Th>Ativar/Inativar</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={6} />
            ) : loadError ? (
              <TableErrorRow colSpan={6} entity="redirecionamentos" onRetry={fetchUrls} />
            ) : urls.length === 0 ? (
              <TableEmptyRow colSpan={6} entity="redirecionamento" />
            ) : (
              urls.map((url) => {
                const fullUrl = `https://abra.com.br${url.from}`

                return (
                  <Tr key={url.id}>
                    <Td>
                      <Flex alignItems="center" gap={2}>
                        {fullUrl}
                        {/* xs de propósito: acompanha o texto da URL, não é a coluna de ações */}
                        <CopyButton value={fullUrl} entity="URL" feminine size="xs" mr={0} />
                      </Flex>
                    </Td>
                    <Td>
                      <ChakraLink href={url.to} target="_blank" rel="noopener noreferrer" color="blue.500">
                        <ShortText text={url.to} />
                      </ChakraLink>
                    </Td>
                    <Td>{url.user.name}</Td>
                    <Td>{formatDateTime(url.created_at)}</Td>
                    <Td>
                      <Switch
                        isChecked={url.active}
                        onChange={(e) => savingRow.run(url.id, () => toggleActive(url, e.target.checked))}
                        isDisabled={!permissions.toggleUrl || savingRow.isPending(url.id)}
                      />
                    </Td>
                    <Td>
                      <ActionButton action="qrcode" onClick={() => setQrUrl(fullUrl)} />
                      {permissions.addUrl && <ActionButton action="duplicate" onClick={() => handleCopy(url)} />}
                      {permissions.toggleUrl && <ActionButton action="edit" onClick={() => setEditingRecord(url.id)} />}
                      {permissions.deleteUrl && <ActionButton action="delete" onClick={() => handleDelete(url.id)} />}
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />
      {permissions.addUrl && (
        <ActionFab label="Adicionar redirecionador de URL" onClick={() => setCreatingRecord(true)} />
      )}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={confirmDelete}
        action="delete"
        entity="redirecionador de URL"
      />
      <UrlRedirectorEditModal id={editingRecord} onClose={() => setEditingRecord(null)} />
      <UrlRedirectorCreateModal isOpen={creatingRecord} onClose={handleCloseCreateModal} initialData={copyingData} />
      <QrCodeModal url={qrUrl} onClose={() => setQrUrl(null)} />
    </>
  )
}

export default UrlRedirectorPage
